import React from "react";
import { Link } from "react-router-dom";
import { 
  Facebook, Instagram, Youtube, Twitter, Linkedin,
  Mail, Phone, MapPin, ArrowRight
} from "lucide-react";
import CNESSLogo from "./CNESSLogo";

const PublicFooter = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    certification: {
      title: "Certification",
      links: [
        { name: "Overview", path: "/certification" },
        { name: "Six Pillars Framework", path: "/framework" },
        { name: "Certification Tiers", path: "/certification#tiers" },
        { name: "Why CNESS Works", path: "/why-cness" },
      ]
    },
    partners: {
      title: "Partners",
      links: [
        { name: "Partner Program", path: "/partner-program" },
        { name: "Become a Partner", path: "/partner-application" },
        { name: "Partner Resources", path: "/partner-resources" },
        { name: "Partner Login", path: "/login" },
      ]
    },
    resources: {
      title: "Resources",
      links: [
        { name: "Success Stories", path: "/success-stories" },
        { name: "Community Hub", path: "/community" },
        { name: "Gallery & Media", path: "/gallery" },
        { name: "Blog", path: "https://cnessofficial.blogspot.com/?m=1", external: true },
        { name: "FAQs", path: "/faqs" },
      ]
    },
    company: {
      title: "Company",
      links: [
        { name: "About Us", path: "/about" },
        { name: "Our Founder", path: "/founder" },
        { name: "Contact", path: "/contact" },
      ]
    }
  };

  const socialLinks = [
    { icon: Facebook, href: "https://www.facebook.com/profile.php?id=61567141033015", label: "Facebook" },
    { icon: Instagram, href: "https://www.instagram.com/cness.inc/", label: "Instagram" },
    { icon: Youtube, href: "https://www.youtube.com/@CNESSinc", label: "YouTube" },
    { icon: Twitter, href: "https://x.com/CnessInc", label: "X (Twitter)" },
    { icon: Linkedin, href: "https://www.linkedin.com/company/cness-india/", label: "LinkedIn" },
  ];

  return (
    <footer className="bg-[#1E3A5F] text-white">
      {/* Main Footer */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block mb-6 bg-white p-3 rounded-xl">
              <CNESSLogo variant="full" />
            </Link>
            <p className="text-white/70 mb-6 leading-relaxed">
              CNESS is a consciousness-based certification and growth platform designed 
              to empower purpose-driven individuals and organizations.
            </p>
            
            {/* Contact Info */}
            <div className="space-y-3">
              <a href="mailto:inquiries@cness.co" className="flex items-center text-white/70 hover:text-[#FDB813] transition-colors">
                <Mail className="w-5 h-5 mr-3" />
                inquiries@cness.co
              </a>
              <a href="tel:+15626435001" className="flex items-center text-white/70 hover:text-[#FDB813] transition-colors">
                <Phone className="w-5 h-5 mr-3" />
                +1 (562) 643-5001
              </a>
              <a href="tel:+918004100108" className="flex items-center text-white/70 hover:text-[#FDB813] transition-colors">
                <Phone className="w-5 h-5 mr-3" />
                +91 1800 410 0108
              </a>
              <div className="flex items-start text-white/70">
                <MapPin className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0" />
                <span>825 Wilshire Blvd #333,<br/>Santa Monica, CA 90401, USA</span>
              </div>
            </div>
            
            {/* Social Links */}
            <div className="flex gap-3 mt-6">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-[#FDB813] hover:text-[#1E3A5F] transition-all"
                  aria-label={social.label}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>
          
          {/* Link Columns */}
          {Object.entries(footerLinks).map(([key, section]) => (
            <div key={key}>
              <h4 className="text-white font-semibold mb-4">{section.title}</h4>
              <ul className="space-y-3">
                {section.links.map((link) => (
                  <li key={link.name}>
                    {link.external ? (
                      <a 
                        href={link.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-white/70 hover:text-[#FDB813] transition-colors text-sm flex items-center gap-1"
                      >
                        {link.name}
                        <span className="text-xs text-[#FDB813]">↗</span>
                      </a>
                    ) : (
                      <Link 
                        to={link.path}
                        className="text-white/70 hover:text-[#FDB813] transition-colors text-sm"
                      >
                        {link.name}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
      
      {/* Newsletter Section */}
      <div className="border-t border-white/10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h4 className="text-white font-semibold mb-1">Stay Connected</h4>
              <p className="text-white/70 text-sm">Get updates on conscious business practices and CNESS news.</p>
            </div>
            <form className="flex gap-2 w-full md:w-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder:text-white/50 focus:outline-none focus:border-[#FDB813] flex-1 md:w-64"
              />
              <button 
                type="submit"
                className="px-6 py-3 bg-[#FDB813] hover:bg-[#e5a611] text-[#1E3A5F] font-semibold rounded-xl transition-colors flex items-center"
              >
                Subscribe
                <ArrowRight className="w-4 h-4 ml-2" />
              </button>
            </form>
          </div>
        </div>
      </div>
      
      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white/50 text-sm">
              © {currentYear} CNESS Inc. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <Link to="/privacy" className="text-white/50 hover:text-white text-sm transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-white/50 hover:text-white text-sm transition-colors">
                Terms & Conditions
              </Link>
              <a 
                href="https://cness.co" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-white/50 hover:text-[#FDB813] text-sm transition-colors"
              >
                Corporate Site →
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default PublicFooter;
